/**
 * @file FormColumnLayout.tsx
 *
 * Generic, single-column form-layout primitives shared by the routed admin
 * forms — a faithful port of the Website Builder v2 `FormColumnLayout` set onto
 * this stack (Gravity utilities + tokens, react-intl). A {@link FormColumnLayout}
 * stacks {@link FormSection}s in a narrow centered-by-inset column; each section
 * is a {@link FormSectionHeading} (title above, optional "Optional" caption) over
 * a bordered {@link FormSectionCard}. Mirrors WSB-v2's 748px column rhythm.
 */

import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import styles from './FormColumnLayout.module.css';

interface FormColumnLayoutProps {
  children: ReactNode;
}

/**
 * Narrow column that vertically stacks form sections with large gaps.
 *
 * @param props.children - The {@link FormSection}s stacked inside the column.
 *
 * @returns The column wrapper.
 */
export function FormColumnLayout({ children }: FormColumnLayoutProps) {
  return (
    <div className={`${styles.column} gv-flex gv-flex-col gv-gap-lg gv-w-full`}>
      {children}
    </div>
  );
}

interface FormSectionProps {
  children: ReactNode;
}

/**
 * One logical block: a heading row over its card body.
 *
 * @param props.children - A {@link FormSectionHeading} and a {@link FormSectionCard}.
 *
 * @returns The section wrapper.
 */
export function FormSection({ children }: FormSectionProps) {
  return <div className="gv-flex gv-flex-col gv-gap-md gv-w-full">{children}</div>;
}

interface FormSectionHeadingProps {
  title: string;
  /** When true, renders a muted "Optional" caption beside the title. */
  showOptional?: boolean;
  /** Optional sub-text rendered below the title (WSB-v2's section description). */
  description?: ReactNode;
}

/**
 * Section title with an optional baseline-aligned "Optional" caption and an
 * optional description line below it.
 *
 * @param props.title - The section label.
 * @param props.showOptional - Shows the "Optional" caption when true.
 * @param props.description - Optional sub-text rendered under the title.
 *
 * @returns The heading block.
 */
export function FormSectionHeading({
  title,
  showOptional = false,
  description,
}: FormSectionHeadingProps) {
  return (
    <div className="gv-flex gv-flex-col gv-gap-xs gv-w-full">
      <div className="gv-flex gv-items-baseline gv-gap-sm gv-flex-wrap gv-w-full">
        <p className="gv-m-0 gv-text-lg gv-text-bold">{title}</p>
        {showOptional ? (
          <span className={`gv-caption-lg ${styles.optional}`}>
            <FormattedMessage {...adminMessages.formOptional} />
          </span>
        ) : null}
      </div>
      {description ? <p className="gv-m-0 gv-text-sm">{description}</p> : null}
    </div>
  );
}

interface FormSectionCardProps {
  children: ReactNode;
  /** Extra utility/module classes merged onto the card shell. */
  className?: string;
}

/**
 * Bordered surface that groups a section's fields.
 *
 * @param props.children - The fields rendered inside the card.
 * @param props.className - Extra classes merged onto the card shell.
 *
 * @returns The card container.
 */
export function FormSectionCard({ children, className = '' }: FormSectionCardProps) {
  return (
    <div
      className={`gv-p-lg gv-surface-bright gv-border gv-radius gv-shadow-subtle gv-flex gv-flex-col gv-w-full gv-gap-md ${className}`.trim()}
    >
      {children}
    </div>
  );
}
